import {
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
} from './types';
import {
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  LISTING_PAGE_PARAM_TYPE_NEW,
  createSlug,
} from './urlHelpers';
import { IMAGE_FIELD_VARIANTS } from './avatarImage';
import { firstDescriptionSentence } from './listingCardCopy';
import { firstUsableListingImage } from './listingCardPhoto';
import { isAvailableNowLivePlan } from './listenerHours';
import { TALK_SESSION_LISTING_TYPE } from './sessionPricing';

export const LISTENING_PROFILE_READY = 'ready';
export const LISTENING_PROFILE_EMPTY = 'empty';
export const LISTENING_PROFILE_BUSY = 'busy';
export const LISTENING_PROFILE_LOADING = 'loading';

export const NIL_LISTING_UUID = '00000000-0000-0000-0000-000000000000';

export const OWN_LISTING_QUERY_STATES = [
  LISTING_STATE_PUBLISHED,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_DRAFT,
  LISTING_STATE_CLOSED,
];

const STATE_RANK = {
  [LISTING_STATE_PUBLISHED]: 0,
  [LISTING_STATE_PENDING_APPROVAL]: 1,
  [LISTING_STATE_DRAFT]: 2,
  [LISTING_STATE_CLOSED]: 3,
};

/**
 * True for the Sharetribe “new draft” placeholder id used by /l/new.
 *
 * @param {string|{uuid?: string}|null|undefined} id
 * @returns {boolean}
 */
export const isNilListingId = id => {
  const uuid = typeof id === 'string' ? id : id?.uuid;
  return !uuid || uuid === NIL_LISTING_UUID;
};

/**
 * Marketplace API ownListings.query params that include drafts.
 * Published-only queries left existing listeners looking like they had none.
 *
 * @returns {{ states: string[], page: number, perPage: number }}
 */
export const ownListeningProfileQueryParams = () => ({
  states: [...OWN_LISTING_QUERY_STATES],
  include: ['images', 'author', 'author.profileImage'],
  'fields.listing': ['title', 'description', 'state', 'availabilityPlan', 'publicData'],
  'fields.image': [...IMAGE_FIELD_VARIANTS],
  page: 1,
  perPage: 20,
});

const listingIdOf = listing => listing?.id?.uuid || listing?.id || null;

/**
 * Own listing id as a UUID string. Summaries store `id` as a string.
 *
 * @param {Object} [listing]
 * @returns {string|null}
 */
export const listingIdFromOwnListing = listing => {
  const id = listingIdOf(listing);
  if (typeof id !== 'string' || isNilListingId(id)) {
    return null;
  }
  return id;
};

/**
 * Shape Available to listen can toggle (always `id.uuid`).
 *
 * @param {Object} [listing]
 * @returns {Object|null}
 */
export const asOwnListingForToggle = listing => {
  const id = listingIdFromOwnListing(listing);
  if (!id) {
    return null;
  }
  if (listing?.id?.uuid) {
    return listing;
  }
  return {
    ...listing,
    id: { uuid: id },
    attributes: listing.attributes || {
      title: listing.title || '',
      state: listing.state,
      publicData: listing.publicData || {},
    },
  };
};

const listingStateOf = listing => listing?.attributes?.state || listing?.state || null;

/**
 * Prefer a published Listening profile, then pending, draft, closed.
 *
 * @param {Array<Object>} listings
 * @returns {Object|null}
 */
export const pickOwnListeningProfile = (listings = []) => {
  const usable = (Array.isArray(listings) ? listings : []).filter(
    listing => listingIdOf(listing) && !isNilListingId(listingIdOf(listing))
  );
  if (usable.length === 0) {
    return null;
  }
  return [...usable].sort((a, b) => {
    const rankA = STATE_RANK[listingStateOf(a)] ?? 9;
    const rankB = STATE_RANK[listingStateOf(b)] ?? 9;
    return rankA - rankB;
  })[0];
};

/**
 * Compact own-profile record stored on the user duck.
 *
 * @param {Object} listing
 * @returns {{ id: string, title: string, slug: string, state: string, images?: Array }|null}
 */
export const summarizeOwnListeningProfile = listing => {
  const id = listingIdOf(listing);
  if (!id || isNilListingId(id)) {
    return null;
  }
  const title = listing?.attributes?.title || listing?.title || '';
  const description =
    listing?.description ||
    firstDescriptionSentence(listing?.attributes?.description || listing?.description || '');
  const cover = firstUsableListingImage(listing);
  return {
    id,
    title,
    slug: createSlug(title),
    state: listingStateOf(listing) || LISTING_STATE_DRAFT,
    description,
    ...(cover ? { images: [cover] } : {}),
    // First photo as uploaded (even if it is a brand mark) for the header avatar fallback.
    ...(listing?.images?.[0] ? { firstImage: listing.images[0] } : {}),
  };
};

const asSummary = listingOrSummary => {
  if (!listingOrSummary) {
    return null;
  }
  if (listingOrSummary.id && listingOrSummary.state && !listingOrSummary.attributes) {
    return isNilListingId(listingOrSummary.id) ? null : listingOrSummary;
  }
  return summarizeOwnListeningProfile(listingOrSummary);
};

/**
 * True when this account has a published or pending Listening profile.
 *
 * @param {Object} [params]
 * @returns {boolean}
 */
export const hasPublishedListeningProfile = ({
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
} = {}) => {
  const summary = asSummary(currentUserOwnListing) || pickOwnListeningProfile(ownListings);
  const state = summary?.state;
  if (state === LISTING_STATE_PUBLISHED || state === LISTING_STATE_PENDING_APPROVAL) {
    return true;
  }
  return false;
};

/**
 * True when this account already has an own Listening profile (any state).
 *
 * @param {Object} [params]
 * @param {boolean} [params.currentUserHasListings]
 * @param {Object|null} [params.currentUserOwnListing]
 * @param {Array} [params.ownListings]
 * @returns {boolean}
 */
export const hasOwnListeningProfile = ({
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
} = {}) => {
  if (currentUserHasListings) {
    return true;
  }
  if (asSummary(currentUserOwnListing)) {
    return true;
  }
  return !!pickOwnListeningProfile(ownListings);
};

/**
 * What the Listening profile card should show. A failed or unfinished
 * lookup is never “not set up”.
 *
 * @param {Object} [params]
 * @returns {{ status: string, listing: Object|null }}
 */
export const listeningProfileCardState = ({
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
  currentUserHasListingsFetched = false,
  currentUserHasListingsError = null,
} = {}) => {
  const listing = asSummary(currentUserOwnListing) || pickOwnListeningProfile(ownListings);
  if (listing) {
    return { status: LISTENING_PROFILE_READY, listing };
  }
  if (currentUserHasListings || currentUserHasListingsError) {
    return { status: LISTENING_PROFILE_BUSY, listing: null };
  }
  if (!currentUserHasListingsFetched) {
    return { status: LISTENING_PROFILE_LOADING, listing: null };
  }
  return { status: LISTENING_PROFILE_EMPTY, listing: null };
};

/**
 * NamedLink params to edit an existing Listening profile. Never the nil-UUID path.
 *
 * @param {Object} listingOrSummary
 * @returns {{ name: string, params: Object }|null}
 */
export const editListeningProfileRoute = listingOrSummary => {
  const summary = asSummary(listingOrSummary);
  if (!summary) {
    return null;
  }
  const isDraft = summary.state === LISTING_STATE_DRAFT;
  return {
    name: 'EditListingPage',
    params: {
      slug: summary.slug || 'draft',
      id: summary.id,
      type: isDraft ? LISTING_PAGE_PARAM_TYPE_DRAFT : LISTING_PAGE_PARAM_TYPE_EDIT,
      tab: 'details',
    },
  };
};

/**
 * Public view of a published / pending Listening profile.
 *
 * @param {Object} listingOrSummary
 * @returns {{ name: string, params: Object }|null}
 */
export const viewListeningProfileRoute = listingOrSummary => {
  const summary = asSummary(listingOrSummary);
  if (!summary) {
    return null;
  }
  if (summary.state === LISTING_STATE_DRAFT || summary.state === LISTING_STATE_CLOSED) {
    return null;
  }
  return {
    name: 'ListingPage',
    params: {
      slug: summary.slug || 'profile',
      id: summary.id,
    },
  };
};

/**
 * Tx/booking header destination for the other party's public profile.
 * Talker (customer) opens the listener ListingPage. Listener (provider)
 * opens the talker ProfilePage. Bookings stay tappable even if the
 * listing is later closed or still a draft — unlike viewListeningProfileRoute.
 *
 * @param {Object} [params]
 * @param {boolean} [params.isCustomer] current user is the talker on this booking
 * @param {Object} [params.listing] the booked Listening listing
 * @param {Object} [params.customer] the talker user
 * @returns {{ name: string, params: Object }|null}
 */
export const bookingOtherPartyProfileRoute = ({ isCustomer, listing, customer } = {}) => {
  if (isCustomer) {
    if (listing?.attributes?.deleted) {
      return null;
    }
    const id = listingIdFromOwnListing(listing);
    if (id) {
      const title = listing?.attributes?.title || listing?.title || '';
      return {
        name: 'ListingPage',
        params: { slug: createSlug(title) || 'profile', id },
      };
    }
    return viewListeningProfileRoute(listing);
  }
  const id = customer?.id?.uuid || customer?.id;
  if (typeof id === 'string' && id) {
    return { name: 'ProfilePage', params: { id } };
  }
  return null;
};

const isPublishedOrPendingListing = listing => {
  if (listing?.attributes?.deleted) {
    return false;
  }
  const state = listingStateOf(listing);
  return state === LISTING_STATE_PUBLISHED || state === LISTING_STATE_PENDING_APPROVAL;
};

const listingTypeOf = listing => listing?.attributes?.publicData?.listingType || null;

/**
 * True for a talk-session Listening profile, or a listing with no listingType
 * (legacy / incomplete publicData that is still the author's public profile).
 *
 * @param {Object} listing
 * @returns {boolean}
 */
export const isPublicListeningProfileListing = listing => {
  const listingType = listingTypeOf(listing);
  return !listingType || listingType === TALK_SESSION_LISTING_TYPE;
};

/**
 * Public Listening listing page for View on a Listening profile card.
 * Prefers talksession, then any published/pending listing so a missing listingType
 * still opens the listing instead of a name/avatar stub. Do not use this for
 * /u/:id — that path is the Talking profile.
 *
 * @param {Array<Object>} listings
 * @returns {{ name: string, params: Object }|null}
 */
export const publicListeningProfileRoute = (listings = []) => {
  const usable = (Array.isArray(listings) ? listings : []).filter(isPublishedOrPendingListing);
  const preferred = usable.filter(isPublicListeningProfileListing);
  return viewListeningProfileRoute(pickOwnListeningProfile(preferred.length ? preferred : usable));
};

/**
 * Account-menu destination for “View your Listening profile”.
 * Public listing when published/pending; otherwise Manage listings — never hide
 * the item for Listen / Both just because the own-listing summary is a draft.
 *
 * @param {Object} listingOrSummary
 * @returns {{ name: string, params?: Object }}
 */
export const viewListeningProfileNavRoute = listingOrSummary =>
  viewListeningProfileRoute(listingOrSummary) || { name: 'ManageListingsPage' };

/**
 * Dashboard welcome avatar destination for the Listening tab.
 * Public listing when it exists; otherwise Create Profile.
 *
 * @param {Object} [listingOrSummary]
 * @returns {{ name: string, params?: Object }}
 */
export const welcomeListeningProfileRoute = listingOrSummary =>
  viewListeningProfileRoute(listingOrSummary) || { name: 'NewListingPage' };

/**
 * Dashboard welcome avatar destination for the Talking tab.
 *
 * @param {Object} [user]
 * @returns {{ name: string, params?: Object, to?: Object }}
 */
export const welcomeTalkingProfileRoute = user => {
  const id = user?.id?.uuid || user?.id;
  if (typeof id === 'string' && id) {
    return { name: 'ProfilePage', params: { id } };
  }
  return { name: 'YourProfilePage', to: { search: '?edit=talking' } };
};

/**
 * Dual-role toggle destination for the owner's Talking profile.
 * /u/:id when the account exists; talking setup if it does not.
 *
 * @param {Object} [user]
 * @returns {{ name: string, params?: Object, search?: string, searchParams?: Object }}
 */
export const switchToOwnTalkingProfileRoute = user => {
  const id = user?.id?.uuid || user?.id;
  if (typeof id === 'string' && id) {
    return { name: 'ProfilePage', params: { id } };
  }
  return {
    name: 'YourProfilePage',
    search: '?edit=talking',
    searchParams: { edit: 'talking' },
  };
};

/**
 * Dual-role toggle destination for the owner's Listening profile.
 * Public listing when published/pending, edit/draft wizard if that exists,
 * otherwise the create-profile prompt.
 *
 * @param {Object} [listingOrSummary]
 * @returns {{ name: string, params?: Object }}
 */
export const switchToOwnListeningProfileRoute = listingOrSummary =>
  viewListeningProfileRoute(listingOrSummary) ||
  editListeningProfileRoute(listingOrSummary) || { name: 'NewListingPage' };

/**
 * Prefer the cached own-listing summary; fall back to listings already
 * loaded on the Talking profile so the toggle still finds /l/:slug/:id
 * when the own-listings query has not finished.
 *
 * @param {Object|null|undefined} stored
 * @param {Array} [listings]
 * @returns {Object|null}
 */
export const ownListingForNav = (stored, listings = []) =>
  asSummary(stored) || pickOwnListeningProfile(listings) || null;

/**
 * Account-menu destination for “Your Listening profile” — always the edit wizard
 * when a profile exists, otherwise Manage listings.
 *
 * @param {Object} listingOrSummary
 * @returns {{ name: string, params?: Object }}
 */
export const editListeningProfileNavRoute = listingOrSummary =>
  editListeningProfileRoute(listingOrSummary) || { name: 'ManageListingsPage' };

/**
 * Edit wizard opened on the availability tab.
 *
 * @param {Object} listingOrSummary
 * @returns {{ name: string, params: Object }|null}
 */
/**
 * Weekly hours the listener edits. Available now is presence only. A leftover
 * 24/7 live plan from the old overlay still yields the stashed weekly hours.
 *
 * @param {Object} [listing]
 * @returns {Object}
 */
export const listenerWeeklyPlanForEditor = listing => {
  const attrs = listing?.attributes || {};
  const publicData = attrs.publicData || listing?.publicData || {};
  const live = attrs.availabilityPlan || listing?.availabilityPlan || {};
  const saved = publicData.slPlanBeforeAvailableNow;
  const savedUsable = saved && Array.isArray(saved.entries) && saved.entries.length > 0;
  const liveIsAvailableNow = publicData.availableNow === true || isAvailableNowLivePlan(live);
  if (liveIsAvailableNow && savedUsable) {
    return saved;
  }
  if (isAvailableNowLivePlan(live)) {
    return {
      type: 'availability-plan/time',
      timezone: live.timezone || saved?.timezone || '',
      entries: [],
    };
  }
  return live;
};

export const editListeningAvailabilityRoute = listingOrSummary => {
  const route = editListeningProfileRoute(listingOrSummary);
  if (!route) {
    return null;
  }
  return {
    ...route,
    params: { ...route.params, tab: 'availability' },
  };
};

/**
 * Featured controls live on Dashboard. Hide the link on the first nil-UUID draft.
 *
 * @param {Object} [params]
 * @param {Object} [params.listing]
 * @param {string} [params.listingType] new | draft | edit
 * @param {boolean} [params.currentUserHasListings]
 * @param {Object} [params.currentUserOwnListing]
 * @returns {boolean}
 */
export const canOpenFeaturedDashboard = ({
  listing,
  listingType,
  currentUserHasListings = false,
  currentUserOwnListing = null,
} = {}) => {
  const publishedOwn =
    currentUserHasListings &&
    currentUserOwnListing?.state &&
    currentUserOwnListing.state !== LISTING_STATE_DRAFT;
  if (listingType === LISTING_PAGE_PARAM_TYPE_NEW || isNilListingId(listing?.id)) {
    return !!publishedOwn;
  }
  const state = listing?.attributes?.state;
  return state === LISTING_STATE_PUBLISHED || state === LISTING_STATE_PENDING_APPROVAL;
};

export const FEATURED_DASHBOARD_HASH = '#featured';

/**
 * In-app Dashboard link for Featured. Listening tab + #featured so Both
 * users do not land on Talking with a dead hash, and SPA routing keeps
 * the signed-in session (no Sign up bounce).
 *
 * @returns {{ search: string, hash: string }}
 */
export const featuredDashboardTo = () => ({
  search: '?tab=listening',
  hash: FEATURED_DASHBOARD_HASH,
});
